// The activity form's layout-driven fields, shared by the create modal and the Field Agent's
// log_activity proposal editor. Presentational: every value and handler arrives as a prop, so each
// caller keeps its own store, form instance and layout choice.
//
// The ConditionalFormField children are load-bearing, not decoration. Without the activity-type
// child, FormFields' fallback renders a generic text input registered as "activityType", while
// useFormSchema watches "activityTypeId" — so the activity-type child layout would silently never
// re-resolve, and that re-resolution is the whole reason a caller uses this instead of its own
// inputs.
import React, { ReactNode, useCallback, useMemo } from "react";

import { FormInstance } from "antd/es/form";
import Select from "antd/es/select";
import cn from "classnames";
import { useIntl } from "react-intl";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import stringHash from "@mapmycustomers/shared/util/hash/stringHash";
import { FormItem, SelectField, TextAreaWithMentions } from "@mapmycustomers/ui";

import ActivityNameField from "@app/component/activity/ActivityNameField";
import AssigneeSearch from "@app/component/activity/AssigneeSearch";
import LockedActivityType from "@app/component/activity/LockedFormFields/LockedActivityType";
import lockedFormFieldsMessages from "@app/component/activity/LockedFormFields/messages";
import useActivityTypeValidationRules from "@app/component/activity/utils/useActivityTypeValidationRules";
import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import FormFields from "@app/component/FormFields";
import {
  CompanyRelationshipField,
  DealRelationshipField,
  PersonRelationshipField,
  usePeopleFieldRules,
} from "@app/component/FormFields/components/Associations";
import RelateEntitiesRequestPayload from "@app/component/FormFields/components/Associations/RelateEntitiesRequestPayload";
import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import AssociationsState from "@app/store/associations/AssociationsState";
import ChangeAssociationsPayload from "@app/store/associations/ChangeAssociationsPayload";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import useActivityVisibilityOptions from "@app/util/hook/useActivityVisibilityOptions";
import { getActivityVisibilityNote } from "@app/util/ui";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import styles from "../../CreateEditActivityModal.module.scss";
import FormValues from "../../types/FormValues";
import File from "../File";

import messages from "./messages";

// LayoutSchemaField.field values (platform names), not the ActivityFieldName form-field names the
// ConditionalFormField children match on.
const RELATIONSHIP_SCHEMA_FIELDS = ["accountId", "contactId", "contacts", "dealId"];
const VISIBILITY_SCHEMA_FIELD = "visibility";

export interface ActivityAssociationsProps {
  associations: AssociationsState;
  fixedCompany?: Company;
  fixedDeal?: Deal;
  fixedPerson?: Person;
  isMultiContactEnabled: boolean;
  onChangeAssociatedEntities: (payload: Omit<ChangeAssociationsPayload, "entityType">) => void;
  onRelateUnrelateEntities: (payload: RelateEntitiesRequestPayload) => void;
}

export interface ActivityFormFieldsProps {
  activityTypeExtra?: ReactNode;
  activityTypes: ActivityType[];
  allowedUserEmails?: string[];
  appendAddableFieldsWithFilter?: (field: IField) => boolean;
  // Absent = the relationship fields are dropped from the schema, so nothing renders for them.
  // The chat editor has no associations owner and needs none: the backend rebuilds record linkage
  // itself.
  associations?: ActivityAssociationsProps;
  canAddField?: (field: IField) => boolean;
  // FormFields matches each child against a schema field and renders only ConditionalFormField
  // children, so anything else passed here is silently dropped.
  children?: ReactNode;
  coachingTaskTypeId?: ActivityType["id"];
  // Fields to render beyond the layout's own. FormFields drops any that the resolved layout already
  // has, so a caller can pass every field it cares about and let the dedupe sort it out.
  defaultAddedFields?: LayoutSchemaField[];
  disallowAdding?: boolean;
  filterFields?: (schemaField: LayoutSchemaField) => boolean;
  form: FormInstance<FormValues>;
  ignoreRequired?: boolean;
  layout: FormLayout;
  lockedActivityType?: ActivityType;
  onActivityNameChange?: () => void;
  teams: Team[];
  users: User[];
  // Absent = the visibility field is dropped from the schema. The chat editor lets the platform
  // default apply.
  visibilityOptions?: ReturnType<typeof useActivityVisibilityOptions>;
}

const ActivityFormFields = ({
  activityTypeExtra,
  activityTypes,
  allowedUserEmails,
  appendAddableFieldsWithFilter,
  associations,
  canAddField,
  children,
  coachingTaskTypeId,
  defaultAddedFields,
  disallowAdding,
  filterFields,
  form,
  ignoreRequired,
  layout,
  lockedActivityType,
  onActivityNameChange,
  teams,
  users,
  visibilityOptions,
}: ActivityFormFieldsProps) => {
  const intl = useIntl();

  const isCheckInActivity = false;

  const activityTypeValidationRules = useActivityTypeValidationRules(intl);

  const personRelationshipFieldRules = usePeopleFieldRules(intl);

  const { fixedCompany, fixedDeal, fixedPerson } = associations ?? {};
  const associationsState = associations?.associations;

  const associatedPeople = useMemo(
    () => (associationsState?.associatedPerson ? [associationsState.associatedPerson] : undefined),
    [associationsState?.associatedPerson]
  );
  const noAssociations = useMemo(
    () =>
      !associationsState?.associatedCompany &&
      !associationsState?.associatedDeal &&
      !associationsState?.associatedPerson,
    [
      associationsState?.associatedCompany,
      associationsState?.associatedDeal,
      associationsState?.associatedPerson,
    ]
  );
  const activityTypeOptions = useActivityTypeOptions(
    activityTypes,
    form.getFieldValue("activityTypeId"),
    useCallback(
      (type: ActivityType) => !type.active || (isCheckInActivity && !type.checkInEnabled),
      [isCheckInActivity]
    )
  );

  const handleAssigneeChange = useCallback(
    (user?: User) => {
      associations?.onChangeAssociatedEntities({ assignee: user });
    },
    [associations]
  );

  // No list means no restriction, matching how AssigneeSearch reads the same absent prop. Testing
  // membership alone would disable every mention, and hideDisabledUsers would empty the list.
  const handleIsMentionDisabled = useCallback(
    (user: User) => !!allowedUserEmails && !allowedUserEmails.includes(user.username),
    [allowedUserEmails]
  );

  // FormFields walks the schema, not our children: a schema field whose ConditionalFormField we
  // skip still renders, through the generic fallback. That turns visibility into a free-text input
  // and makes each relationship id log an error and render null. Dropping the field from the schema
  // is what actually hides it, so the guards below and the ones in the tree stay in step.
  const hasAssociations = !!associations;
  const hasVisibilityOptions = !!visibilityOptions;
  const composedFilterFields = useCallback(
    (schemaField: LayoutSchemaField) => {
      if (!hasAssociations && RELATIONSHIP_SCHEMA_FIELDS.includes(schemaField.field)) {
        return false;
      }
      if (!hasVisibilityOptions && schemaField.field === VISIBILITY_SCHEMA_FIELD) {
        return false;
      }
      return filterFields?.(schemaField) ?? true;
    },
    [filterFields, hasAssociations, hasVisibilityOptions]
  );

  return (
    <FormFields
      appendAddableFieldsWithFilter={appendAddableFieldsWithFilter}
      canAddField={canAddField}
      defaultAddedFields={defaultAddedFields}
      disallowAdding={disallowAdding}
      entityType={EntityType.ACTIVITY}
      fileComponent={File}
      filterFields={composedFilterFields}
      ignoreRequired={ignoreRequired}
      // Both callers create an activity; nothing here renders an existing one.
      isCreateForm
      layout={layout}
    >
      <ConditionalFormField feature={FieldFeature.ACTIVITY_TYPE_FIELD}>
        {lockedActivityType ? (
          <>
            <LockedActivityType
              activityType={lockedActivityType}
              lockTooltip={
                lockedActivityType.id === coachingTaskTypeId
                  ? intl.formatMessage(lockedFormFieldsMessages.activityTypeLockTooltipCoachingTask)
                  : undefined
              }
            />
            {activityTypeExtra}
          </>
        ) : (
          ({ disabled, label }: ConditionalFormFieldRenderProps) => (
            <>
              <FormItem
                label={label}
                name="activityTypeId"
                required
                rules={activityTypeValidationRules}
                showLabel={false}
              >
                <SelectField<ActivityType["id"]>
                  className={styles.select}
                  disabled={disabled}
                  dropdownMatchSelectWidth={false}
                  filterOption={(inputValue, option) =>
                    (
                      activityTypes.find((type) => type.id === parseInt(`${option?.value || 0}`))
                        ?.name as string
                    )
                      ?.toLowerCase()
                      .includes(inputValue.toLowerCase().trim()) ?? false
                  }
                  label={label}
                  placeholder={intl.formatMessage(messages.placeholder)}
                  required
                  showSearch
                >
                  {isCheckInActivity && (
                    <Select.Option
                      className={styles.message}
                      disabled
                      key="Activity Types"
                      label={intl.formatMessage(messages.checkInTypesMessage)}
                      value={-1}
                    >
                      {intl.formatMessage(messages.checkInTypesMessage)}
                    </Select.Option>
                  )}
                  {activityTypeOptions.map((type) => (
                    <Select.Option
                      className={cn(styles.option, {
                        [styles.disabled]: type.disabled,
                      })}
                      disabled={type.disabled}
                      key={type.value}
                      label={type.label}
                      value={type.value}
                    >
                      {type.label}
                    </Select.Option>
                  ))}
                </SelectField>
              </FormItem>
              {activityTypeExtra}
            </>
          )
        )}
      </ConditionalFormField>

      <ConditionalFormField fieldName={ActivityFieldName.NAME}>
        {({ disabled }: ConditionalFormFieldRenderProps) => (
          <ActivityNameField
            activityTypes={activityTypes}
            disabled={disabled}
            onChange={onActivityNameChange}
            required
          />
        )}
      </ConditionalFormField>

      <ConditionalFormField fieldName={ActivityFieldName.ASSIGNEE}>
        {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
          <FormItem label={label} name="assignee" required={required} rules={[{ required }]}>
            <AssigneeSearch
              allowedUserEmails={allowedUserEmails}
              disabled={disabled}
              onChange={handleAssigneeChange}
              users={users}
            />
          </FormItem>
        )}
      </ConditionalFormField>

      {associations ? (
        <ConditionalFormField fieldName={ActivityFieldName.COMPANY}>
          {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
            <CompanyRelationshipField
              allowAdd={!(disabled && (fixedCompany || fixedPerson || fixedDeal))}
              allowClear
              associatedCompany={associationsState?.associatedCompany}
              associatedDeal={associationsState?.associatedDeal}
              associatedPerson={associationsState?.associatedPerson}
              disabled={disabled}
              entityType={EntityType.ACTIVITY}
              label={label}
              onChangeAssociatedEntities={associations.onChangeAssociatedEntities}
              relateEntities={associations.onRelateUnrelateEntities}
              required={required}
              suggestedCompanies={
                noAssociations ? undefined : associationsState?.availableCompanies
              }
            />
          )}
        </ConditionalFormField>
      ) : null}

      {associations ? (
        <ConditionalFormField fieldName={[ActivityFieldName.PERSON, ActivityFieldName.PEOPLE]}>
          {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
            <PersonRelationshipField
              allowAdd={!(disabled && (fixedPerson || fixedDeal))}
              allowClear
              associatedCompany={associationsState?.associatedCompany}
              associatedDeal={associationsState?.associatedDeal}
              associatedPeople={associatedPeople}
              disabled={disabled}
              extraRules={personRelationshipFieldRules}
              label={label}
              maxTagCount={1}
              multiselect={associations.isMultiContactEnabled}
              onChangeAssociatedEntities={associations.onChangeAssociatedEntities}
              relateEntities={associations.onRelateUnrelateEntities}
              required={required}
              suggestedPeople={noAssociations ? undefined : associationsState?.availablePeople}
            />
          )}
        </ConditionalFormField>
      ) : null}

      {associations ? (
        <ConditionalFormField fieldName={ActivityFieldName.DEAL}>
          {({ disabled, required }: ConditionalFormFieldRenderProps) => (
            <DealRelationshipField
              allowAdd={!(disabled && fixedDeal)}
              allowClear
              associatedCompany={associationsState?.associatedCompany}
              associatedDeal={associationsState?.associatedDeal}
              associatedPerson={associationsState?.associatedPerson}
              disabled={disabled}
              field={activityFieldModel.getByName(ActivityFieldName.DEAL)!}
              onChangeAssociatedEntities={associations.onChangeAssociatedEntities}
              relateEntities={associations.onRelateUnrelateEntities}
              required={required}
              suggestedDeals={noAssociations ? undefined : associationsState?.availableDeals}
            />
          )}
        </ConditionalFormField>
      ) : null}

      {children}

      <ConditionalFormField fieldName={ActivityFieldName.NOTE}>
        {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
          <FormItem
            noStyle
            shouldUpdate={(prevValues, curValues) =>
              prevValues.note !== curValues.note || prevValues.visibility !== curValues.visibility
            }
          >
            {({ getFieldValue }) => (
              <FormItem
                label={label}
                name={ActivityFieldName.NOTE}
                required={required}
                rules={[{ required }]}
                showLabel={false}
              >
                <TextAreaWithMentions
                  allUsers={users}
                  disabled={disabled}
                  emptyTextForWithSkippedUsers={intl.formatMessage(
                    messages.emptyTextForWithSkippedUsers
                  )}
                  hideDisabledUsers
                  isMentionDisabled={handleIsMentionDisabled}
                  key={
                    getFieldValue(ActivityFieldName.VISIBILITY) +
                    stringHash(allowedUserEmails?.join() ?? "")
                  }
                  label={label}
                  maxLength={Number.MAX_SAFE_INTEGER}
                  required={required}
                  teams={teams}
                />
              </FormItem>
            )}
          </FormItem>
        )}
      </ConditionalFormField>

      {visibilityOptions ? (
        <ConditionalFormField fieldName={ActivityFieldName.VISIBILITY}>
          {({ disabled, required }: ConditionalFormFieldRenderProps) => (
            <FormItem
              label={intl.formatMessage(messages.visibility)}
              required={required}
              shouldUpdate={(prev, next) =>
                prev.visibility !== next.visibility || prev.teamIds !== next.teamIds
              }
            >
              {({ getFieldValue, setFieldsValue }) => (
                <VisibilitySelectField<ActivityVisibility>
                  disabled={disabled}
                  noteGetter={getActivityVisibilityNote}
                  onChange={(visibility: ActivityVisibility) => setFieldsValue({ visibility })}
                  onTeamIdsChange={(teamIds: Team["id"][]) => setFieldsValue({ teamIds })}
                  options={visibilityOptions}
                  teamIds={getFieldValue("teamIds")}
                  teams={teams}
                  teamsRenderer={(node) => (
                    <FormItem
                      name="teamIds"
                      noStyle
                      rules={[
                        {
                          message: intl.formatMessage(messages.teamsRequired),
                          required: true,
                        },
                      ]}
                    >
                      {node}
                    </FormItem>
                  )}
                  value={getFieldValue(ActivityFieldName.VISIBILITY)}
                  visibilityRenderer={(node) => (
                    <FormItem name={ActivityFieldName.VISIBILITY} noStyle>
                      {node}
                    </FormItem>
                  )}
                />
              )}
            </FormItem>
          )}
        </ConditionalFormField>
      ) : null}
    </FormFields>
  );
};

export default ActivityFormFields;
